import {
  exampleMaterial,
  exampleSource,
} from '@/pages/learn/study/studyMaterialLearningExampleData';

const clean = (text: string) =>
  text
    .replace(/×/g, 'x')
    .replace(/÷/g, '/')
    .replace(/[–—]/g, '-')
    .replace(/[^\x20-\x7e]/g, '');
const escape = (text: string) => clean(text).replace(/([\\()])/g, '\\$1');
function wrap(text: string) {
  const lines: string[] = [];
  let current = '';
  for (const word of clean(text).split(/\s+/)) {
    if (`${current} ${word}`.trim().length > 75) {
      lines.push(current);
      current = word;
    } else current = `${current} ${word}`.trim();
  }
  if (current) lines.push(current);
  return lines;
}
export interface SampleSourcePage {
  title: string;
  paragraphs: string[];
}
export function samplePageLayout(page: number, source?: SampleSourcePage[]) {
  let top = 150;
  const blocks = source
    ? (source[page]?.paragraphs ?? []).map((text, index) => ({
        id: `example-source-${page}-${index}`,
        page_index: page,
        text,
      }))
    : exampleSource;
  return blocks
    .filter((block) => block.page_index === page)
    .map((block) => {
      const lines = wrap(block.text),
        y = top;
      top += lines.length * 16 + 26;
      return {
        ...block,
        lines,
        y,
        region: { x: 45 / 595, y: (y - 13) / 842, w: 505 / 595, h: (lines.length * 16 + 12) / 842 },
      };
    });
}
/** Actual, minimal three-page PDF bytes generated from the authored sample source, no provider. */
export function samplePdfUrl(source?: SampleSourcePage[]) {
  const titles = source?.map((page) => page.title) ?? exampleMaterial.pageTitles;
  const objects: string[] = [];
  const put = (value: string) => {
    objects.push(value);
    return objects.length;
  };
  put('<< /Type /Catalog /Pages 2 0 R >>');
  put('');
  put('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');
  const pages: number[] = [];
  for (let page = 0; page < titles.length; page++) {
    const commands = [
      'BT /F1 10 Tf 45 800 Td (FICTIONAL SAMPLE - prepared source, not a learner upload) Tj ET',
      `BT /F1 17 Tf 45 765 Td (${escape(titles[page])}) Tj ET`,
    ];
    for (const block of samplePageLayout(page, source))
      block.lines.forEach((line, index) =>
        commands.push(`BT /F1 12 Tf 45 ${842 - block.y - index * 16} Td (${escape(line)}) Tj ET`),
      );
    commands.push(
      `BT /F1 10 Tf 45 35 Td (Page ${page + 1} of ${titles.length} - Fictional learning sample) Tj ET`,
    );
    const stream = commands.join('\n');
    const content = put(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
    pages.push(
      put(
        `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 3 0 R >> >> /Contents ${content} 0 R >>`,
      ),
    );
  }
  objects[1] = `<< /Type /Pages /Kids [${pages.map((id) => `${id} 0 R`).join(' ')}] /Count ${titles.length} >>`;
  let pdf = '%PDF-1.4\n';
  const offsets = [0];
  objects.forEach((object, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });
  const start = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n${offsets
    .slice(1)
    .map((offset) => `${String(offset).padStart(10, '0')} 00000 n \n`)
    .join('')}trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${start}\n%%EOF\n`;
  return { url: `data:application/pdf;base64,${btoa(pdf)}`, bytes: pdf.length };
}
/** Stored-equivalent local PNG for the formal source viewer. Same text and regions as the PDF. */
export async function samplePageImage(page: number, source?: SampleSourcePage[]) {
  const canvas = document.createElement('canvas');
  canvas.width = 1190;
  canvas.height = 1684;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Sample source image is unavailable.');
  ctx.scale(2, 2);
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, 595, 842);
  ctx.fillStyle = '#1F1B2D';
  ctx.font = '10px Arial';
  ctx.fillText('FICTIONAL SAMPLE · prepared source, not a learner upload', 45, 42);
  ctx.font = 'bold 17px Arial';
  ctx.fillText(source?.[page]?.title ?? exampleMaterial.pageTitles[page], 45, 77);
  ctx.font = '12px Arial';
  for (const block of samplePageLayout(page, source))
    block.lines.forEach((line, index) => ctx.fillText(line, 45, block.y + index * 16));
  ctx.font = '10px Arial';
  ctx.fillText(`Page ${page + 1} of ${source?.length ?? 3} · Fictional learning sample`, 45, 807);
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (value) => (value ? resolve(value) : reject(new Error('Sample page could not be drawn.'))),
      'image/png',
    ),
  );
  return {
    read_url: URL.createObjectURL(blob),
    size_bytes: blob.size,
    mime_type: 'image/png' as const,
  };
}
